import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUsageStore } from '@/stores/usage'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/dashboard'
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/sso-auth',
      name: 'SSOAuth',
      component: () => import('@/views/auth/SSOAuthView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/views/dashboard/DashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/leads',
      name: 'Leads',
      component: () => import('@/views/leads/LeadsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/lead-search',
      name: 'LeadSearchList',
      component: () => import('@/views/leads/LeadSearchListView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/lead-search/config/:workflowId?',
      name: 'LeadSearchConfig',
      component: () => import('@/views/leads/LeadSearchConfigView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/templates',
      name: 'Templates',
      component: () => import('@/views/templates/TemplatesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/sequences',
      name: 'Sequences',
      component: () => import('@/views/sequences/SequencesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/cadences',
      name: 'Cadences',
      component: () => import('@/views/cadences/CadencesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics',
      redirect: '/analytics/dashboard'
    },
    {
      path: '/analytics/dashboard',
      name: 'AnalyticsDashboard',
      component: () => import('@/views/analytics/AnalyticsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics/report',
      name: 'AnalyticsReport',
      component: () => import('@/views/analytics/AnalyticsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/reports',
      name: 'Reports',
      component: () => import('@/views/reports/ReportsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      name: 'Settings',
      component: () => import('@/views/settings/SettingsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings/accounts',
      name: 'SettingsAccounts',
      component: () => import('@/views/settings/SettingsAccountsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/kanban',
      name: 'Kanban',
      component: () => import('@/views/kanban/KanbanView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/NotFoundView.vue')
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Aguarda a inicialização da autenticação na primeira navegação
  if (!authStore.isInitialized) {
    // Se ainda está carregando, aguarda um pouco mais
    let attempts = 0
    const maxAttempts = 50 // 5 segundos máximo

    while (!authStore.isInitialized && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100))
      attempts++
    }
  }

  // 🔍 Debug para rotas protegidas
  if (to.meta.requiresAuth) {
    console.log('🔍 Route guard check:', {
      route: to.path,
      isAuthenticated: authStore.isAuthenticated,
      user: authStore.user,
      ssoAuth: localStorage.getItem('sso_authenticated')
    })
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // 🔧 Tentar restaurar SSO antes de bloquear (exceto se vindo de logout)
    const isLogoutRedirect = from.path === '/login' || to.path === '/login'
    const isExplicitLogout = sessionStorage.getItem('explicit_logout') === 'true'

    if (!isLogoutRedirect && !isExplicitLogout) {
      console.log('🔄 User not authenticated, checking SSO...')
      const ssoRestored = authStore.checkAndMaintainSSO()

      if (ssoRestored) {
        console.log('✅ SSO restored, allowing access to:', to.path)
      } else {
        console.log('❌ No valid SSO found, redirecting to login')
        next('/login')
        return
      }
    } else {
      console.log('🚪 Logout redirect - going to login without SSO check')
      next('/login')
      return
    }
  }

  // ✅ Carregar limites de uso após autenticação
  if (to.meta.requiresAuth && authStore.isAuthenticated) {
    const usageStore = useUsageStore()

    // Buscar limites se ainda não foram carregados ou se passaram mais de 5 minutos
    const shouldFetch =
      !usageStore.lastFetch ||
      Date.now() - usageStore.lastFetch.getTime() > 5 * 60 * 1000

    if (shouldFetch && !usageStore.isLoading) {
      try {
        await usageStore.fetchLimits()
        console.log('✅ Usage limits loaded')
      } catch (error) {
        console.error('❌ Failed to fetch usage limits:', error)
      }
    }
  }

  // Check if route requires guest (not authenticated)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    console.log('✅ Authenticated user trying to access guest route, redirecting to dashboard')
    next('/dashboard')
    return
  }

  // Limpar flag de logout explícito quando acessar a tela de login
  if (to.path === '/login') {
    sessionStorage.removeItem('explicit_logout')
  }

  console.log('✅ Route access granted:', to.path)
  next()
})

export default router
